/**
 * Coloured chips for the hub's admin-configured vocabulary: stages, segments,
 * lifecycle, tags, risk, billing. Colours come from Admin, so chips tint the
 * background and keep text dark for legibility whatever colour is chosen.
 */
import type { BillingStatus, DealFlag, ReplyOutcome, RiskLevel, RiskSignal } from "@otii/shared";
import { BILLING_STATUS_LABEL, REPLY_OUTCOME_LABEL } from "@otii/shared";
import { AlertTriangle, Ban, BadgeCheck, CircleCheck, Eye, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Chip } from "@otii/ui/chip";
import { cn } from "../lib/cn";
import { useConfig } from "../lib/config";
import { Tooltip } from "./overlay";

export { Chip };

export function StageChip({ stageId, className }: { stageId: string | null | undefined; className?: string }) {
  const cfg = useConfig();
  const s = cfg.stage(stageId);
  if (!s) return null;
  return (
    <Chip color={s.color} className={className}>
      {s.name}
    </Chip>
  );
}

export function SegmentChip({ segmentId, siteCount, className }: { segmentId?: string | null; siteCount?: number; className?: string }) {
  const cfg = useConfig();
  const seg = segmentId ? cfg.segment(segmentId) : siteCount !== undefined ? cfg.segmentFor(siteCount) : undefined;
  if (!seg) return null;
  return (
    <Chip color={seg.color} solid className={className} title={`${seg.name} (by site count)`}>
      {seg.name}
    </Chip>
  );
}

export function LifecycleChip({ stageId, showCategory = true, className }: { stageId: string | null | undefined; showCategory?: boolean; className?: string }) {
  const cfg = useConfig();
  const st = cfg.lifecycleStage(stageId);
  const cat = cfg.categoryOfStage(stageId);
  if (!st || !cat) return null;
  return (
    <Chip color={st.color || cat.color} className={className} title={`${cat.name}: ${st.name}`}>
      {showCategory ? (
        <>
          <span className="opacity-70">{cat.name} ·</span> {st.name}
        </>
      ) : (
        st.name
      )}
    </Chip>
  );
}

export function TagChips({ tagIds, max = 3 }: { tagIds: string[]; max?: number }) {
  const cfg = useConfig();
  const tags = tagIds.map((t) => cfg.listItem(t)).filter(Boolean);
  if (!tags.length) return null;
  const shown = tags.slice(0, max);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <Chip key={t!.id} color={t!.color}>
          {t!.name}
        </Chip>
      ))}
      {tags.length > max && (
        <Tooltip content={tags.slice(max).map((t) => t!.name).join(", ")}>
          <span className="text-xs font-semibold text-muted">+{tags.length - max}</span>
        </Tooltip>
      )}
    </span>
  );
}

/** Marks a provider that has converted: its sales deal was won and it's now a customer. */
export function CustomerChip({ className }: { className?: string }) {
  return (
    <Chip color="#16A34A" className={className} title="Converted: the sales deal was won" icon={<BadgeCheck className="size-3.5 shrink-0 text-success-ink" aria-hidden />}>
      Customer
    </Chip>
  );
}

/** The sales deal's status, written back to the provider list. */
export function DealFlagChip({ flag, isCustomer, lifecycleStageId }: { flag: DealFlag | null; isCustomer?: boolean; lifecycleStageId?: string | null }) {
  if (isCustomer && lifecycleStageId) return <LifecycleChip stageId={lifecycleStageId} />;
  if (!flag) return <span className="text-xs text-subtle">No deal</span>;
  return <StageChip stageId={flag.stageId} />;
}

const RISK: Record<RiskLevel, { label: string; cls: string; icon: ReactNode }> = {
  healthy: { label: "Healthy", cls: "bg-success-bg text-success-ink border-success/25", icon: <CircleCheck className="size-3.5" /> },
  watch: { label: "Watch", cls: "bg-warn-bg text-terra-text border-terra-deep/25", icon: <Eye className="size-3.5" /> },
  at_risk: { label: "At risk", cls: "bg-danger-bg text-danger-ink border-danger/25", icon: <AlertTriangle className="size-3.5" /> },
};

export function RiskBadge({ level, signals, className }: { level: RiskLevel; signals?: RiskSignal[]; className?: string }) {
  const r = RISK[level];
  const badge = (
    <span
      tabIndex={signals?.length ? 0 : undefined}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[12px] leading-5 font-bold", r.cls, className)}
    >
      {r.icon}
      {r.label}
      {signals && signals.length > 0 && level !== "healthy" && <span className="opacity-70">· {signals.length}</span>}
    </span>
  );
  if (!signals?.length) return badge;
  return (
    <Tooltip
      content={
        <ul className="space-y-0.5">
          {signals.map((s, i) => (
            <li key={i}>
              {s.level === "at_risk" ? "●" : "○"} {s.label}
            </li>
          ))}
        </ul>
      }
    >
      {badge}
    </Tooltip>
  );
}

const BILLING_TONE: Record<BillingStatus, string> = {
  paid: "#16A34A",
  awaiting_payment: "#3D6F8E",
  overdue: "#DC2626",
  unbilled: "#8C8C8C",
};

export function BillingChip({ status }: { status: BillingStatus | null | undefined }) {
  if (!status) return <span className="text-xs text-subtle">—</span>;
  return <Chip color={BILLING_TONE[status]}>{BILLING_STATUS_LABEL[status]}</Chip>;
}

/** Shown wherever outreach is hard-blocked (GDPR opt-out or suppression). */
export function BlockedBadge({ reason, compact }: { reason?: string | null; compact?: boolean }) {
  const el = (
    <span className="inline-flex items-center gap-1 rounded-full border border-danger/25 bg-danger-bg px-2 py-[1px] text-[12px] leading-5 font-bold text-danger-ink">
      <Ban className="size-3.5" />
      {!compact && "Do not contact"}
    </span>
  );
  return reason ? <Tooltip content={reason}>{el}</Tooltip> : el;
}

export function SuppressedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-danger/25 bg-danger-bg px-2 py-[1px] text-[12px] leading-5 font-bold text-danger-ink">
      <ShieldAlert className="size-3.5" /> Suppressed
    </span>
  );
}

const REPLY_COLOR: Record<ReplyOutcome, string> = {
  interested: "#16A34A",
  not_now: "#D97706",
  no_thanks: "#DC2626",
  wrong_person: "#8C8C8C",
  bounced: "#DC2626",
};

export function ReplyChip({ outcome, className }: { outcome: ReplyOutcome | null; className?: string }) {
  if (!outcome) return null;
  return (
    <Chip color={REPLY_COLOR[outcome]} className={className}>
      {REPLY_OUTCOME_LABEL[outcome]}
    </Chip>
  );
}
